import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Mail, Phone, MapPin, Sparkles, CheckCircle2, Clock, Shield } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
const Contact = () => {
  const {
    toast
  } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast({
      title: "Message sent!",
      description: "We'll get back to you within 24 hours."
    });
    setIsSubmitting(false);
    (e.target as HTMLFormElement).reset();
  };
  const benefits = ["Free AI-readiness audit", "Custom strategy for your industry", "No obligation consultation", "First-mover pricing available"];
  return <section id="contact" className="py-12 md:py-16 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-primary/10 to-accent/10 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 max-w-[1280px] relative">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center space-y-4 mb-10">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 text-sm">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-foreground uppercase tracking-wider text-xs font-semibold">Limited Spots Available</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
            Be The <span className="text-gradient">First In Your Market</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Ready to dominate AI search before your competitors? 
            Get your free AI Ads audit and see how we can help.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }}>
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary/50 to-accent/50 rounded-3xl blur-lg opacity-30" />
              <div className="relative glass rounded-2xl p-6 border-2 border-primary/20">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">GET YOUR FREE AUDIT</h3>
                    <p className="text-xs text-muted-foreground">Takes only 30 seconds</p>
                  </div>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Input placeholder="Your Name *" required className="bg-secondary/50 border-border/50 h-11 focus:border-primary transition-colors" />
                    <Input type="email" placeholder="Email Address *" required className="bg-secondary/50 border-border/50 h-11 focus:border-primary transition-colors" />
                  </div>
                  
                  <Input placeholder="Business Name *" required className="bg-secondary/50 border-border/50 h-11 focus:border-primary transition-colors" />
                  
                  <Input placeholder="Phone Number (optional)" className="bg-secondary/50 border-border/50 h-11 focus:border-primary transition-colors" />
                  
                  <Textarea placeholder="Tell us about your business and goals... (optional)" rows={2} className="bg-secondary/50 border-border/50 resize-none focus:border-primary transition-colors" />
                  
                  <Button type="submit" size="lg" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.02] transition-all h-12 text-base">
                    {isSubmitting ? "SENDING..." : "GET MY FREE AUDIT"}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>

                  <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground pt-1">
                    <span className="flex items-center gap-1">
                      <Shield className="h-3 w-3" /> No spam, ever
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Response in 2hrs
                    </span>
                  </div>
                </form>

                {/* Benefits list */}
                <div className="mt-5 pt-5 border-t border-border/50">
                  <p className="text-sm font-semibold mb-2">What you'll get:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {benefits.map((benefit, index) => <div key={index} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 text-accent shrink-0" />
                        <span>{benefit}</span>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="space-y-4">
            <div className="glass rounded-2xl p-5">
              <h3 className="text-base font-semibold mb-2">Prefer to reach out directly?</h3>
              <p className="text-muted-foreground text-sm">
                We're here to help. Reach out via email or phone, and we'll get back to you within 24 hours.
              </p>
            </div>

            <div className="space-y-3">
              <a href="mailto:hello@aiadsflow.com" className="flex items-center gap-4 glass rounded-xl p-4 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all group">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-sm group-hover:text-primary transition-colors">EMAIL US</p>
                  <p className="text-sm text-muted-foreground">support@aiadsflow.com</p>
                </div>
              </a>

              <a href="tel:+1234567890" className="flex items-center gap-4 glass rounded-xl p-4 hover:border-accent/50 hover:shadow-lg hover:shadow-accent/5 transition-all group">
                <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Phone className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <p className="font-medium text-sm group-hover:text-accent transition-colors">CALL US</p>
                  <p className="text-sm text-muted-foreground">(407) 545-2129</p>
                </div>
              </a>

              <div className="flex items-center gap-4 glass rounded-xl p-4">
                <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium text-sm">LOCATION</p>
                  <p className="text-sm text-muted-foreground">Serving businesses across USA & Canada</p>
                </div>
              </div>
            </div>

            <div className="glass rounded-xl p-5 text-center bg-gradient-to-br from-primary/5 to-accent/5">
              <p className="text-sm text-muted-foreground mb-1">Average response time</p>
              <p className="text-2xl font-bold text-gradient">Under 2 Hours</p>
              <p className="text-xs text-muted-foreground mt-1">We take every inquiry seriously</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};
export default Contact;